/**
 * WebGPU/TSL runtime matcap material matching {@link MatcapShaderMaterial} behavior
 * (reveal, ground clip, indirect tint) on top of {@link MeshMatcapNodeMaterial}.
 */

import * as THREE from 'three';
import {
  clamp,
  diffuseColor,
  dot,
  float,
  Fn,
  length,
  mix,
  modelWorldMatrix,
  modelWorldMatrixInverse,
  normalize,
  normalWorld,
  or,
  positionLocal,
  positionWorld,
  pow,
  select,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';
import { MeshMatcapNodeMaterial } from 'three/webgpu';

import type { MatcapShaderMaterial } from './MatcapShaderMaterial.js';

/**
 * Node matcap used as the WebGPU runtime counterpart to {@link MatcapShaderMaterial}.
 * Not serialized — instantiated only from a live source material.
 */
export class GenesysMatcapNodeMaterial extends MeshMatcapNodeMaterial {
  /** Back-link for resolver/sync; set when created from {@link MatcapShaderMaterial}. */
  declare userData: THREE.Material['userData'] & {
    genesysMatcapSource?: MatcapShaderMaterial;
  };

  readonly uRevealProgress = uniform(float(1));
  readonly uEnableReveal = uniform(float(0));
  readonly uIndirectDistanceAmplitude = uniform(float(1.75));
  readonly uIndirectDistanceStrength = uniform(float(0.5));
  readonly uIndirectDistancePower = uniform(float(2));
  readonly uIndirectAngleStrength = uniform(float(1.5));
  readonly uIndirectAngleOffset = uniform(float(0.6));
  readonly uIndirectAnglePower = uniform(float(1));
  readonly uIndirectColor = uniform(new THREE.Color('#d04500'));
  readonly uClipBelowGround = uniform(float(0));
  readonly uGroundY = uniform(float(0));

  constructor(source: MatcapShaderMaterial) {
    super();

    this.userData.genesysMatcapSource = source;

    // Render state is read by the renderer from the source (renderObject.material),
    // and base material properties (color/matcap/opacity) are read by Three's TSL
    // via `builder.material` (also the source). So we only need to forward the
    // custom shader uniforms onto the derived node material each frame.
    this.uRevealProgress.onObjectUpdate(() => (this.uRevealProgress.value = source.uniforms.uRevealProgress.value as number));
    this.uEnableReveal.onObjectUpdate(() => (this.uEnableReveal.value = source.uniforms.uEnableReveal.value as number));
    this.uIndirectDistanceAmplitude.onObjectUpdate(() => (this.uIndirectDistanceAmplitude.value = source.uniforms.uIndirectDistanceAmplitude.value as number));
    this.uIndirectDistanceStrength.onObjectUpdate(() => (this.uIndirectDistanceStrength.value = source.uniforms.uIndirectDistanceStrength.value as number));
    this.uIndirectDistancePower.onObjectUpdate(() => (this.uIndirectDistancePower.value = source.uniforms.uIndirectDistancePower.value as number));
    this.uIndirectAngleStrength.onObjectUpdate(() => (this.uIndirectAngleStrength.value = source.uniforms.uIndirectAngleStrength.value as number));
    this.uIndirectAngleOffset.onObjectUpdate(() => (this.uIndirectAngleOffset.value = source.uniforms.uIndirectAngleOffset.value as number));
    this.uIndirectAnglePower.onObjectUpdate(() => (this.uIndirectAnglePower.value = source.uniforms.uIndirectAnglePower.value as number));
    this.uClipBelowGround.onObjectUpdate(() => (this.uClipBelowGround.value = source.uniforms.uClipBelowGround.value as number));
    this.uGroundY.onObjectUpdate(() => (this.uGroundY.value = source.uniforms.uGroundY.value as number));
    this.uIndirectColor.onObjectUpdate(() => this.uIndirectColor.value.copy(source.uniforms.uIndirectColor.value as THREE.Color));

    const uRevealProgress = this.uRevealProgress;
    const uEnableReveal = this.uEnableReveal;
    const uIndirectDistanceAmplitude = this.uIndirectDistanceAmplitude;
    const uIndirectDistanceStrength = this.uIndirectDistanceStrength;
    const uIndirectDistancePower = this.uIndirectDistancePower;
    const uIndirectAngleStrength = this.uIndirectAngleStrength;
    const uIndirectAngleOffset = this.uIndirectAngleOffset;
    const uIndirectAnglePower = this.uIndirectAnglePower;
    const uIndirectColor = this.uIndirectColor;
    const uClipBelowGround = this.uClipBelowGround;
    const uGroundY = this.uGroundY;

    // Reveal: match GLSL world-space Y offset, expressed as adjusted local position.
    this.positionNode = Fn(() => {
      const worldPos = modelWorldMatrix.mul(vec4(positionLocal, 1.0)).xyz;
      const dist = length(worldPos);
      let revealProg = uRevealProgress.sub(dist.div(30.0)).mul(5.0);
      revealProg = float(1.0).sub(clamp(revealProg, float(-0.1), float(1.0)));
      revealProg = pow(revealProg, float(2.0));
      revealProg = select(uRevealProgress.greaterThan(0.99), float(0.0), revealProg);
      const yAmplitude = float(3.2);
      const revealOffset = revealProg.mul(yAmplitude).mul(uEnableReveal);
      const revealedWorld = vec3(worldPos.x, worldPos.y.sub(revealOffset), worldPos.z);
      return modelWorldMatrixInverse.mul(vec4(revealedWorld, 1.0)).xyz;
    })();

    // Below-ground discard (fragment), matching `uClipBelowGround` / `uGroundY`.
    this.maskNode = or(
      uClipBelowGround.lessThanEqual(0.5),
      positionWorld.y.greaterThanEqual(uGroundY)
    );
  }

  override setupVariants(builder: unknown): void {
    super.setupVariants(builder as never);

    const uIndirectDistanceAmplitude = this.uIndirectDistanceAmplitude;
    const uIndirectDistanceStrength = this.uIndirectDistanceStrength;
    const uIndirectDistancePower = this.uIndirectDistancePower;
    const uIndirectAngleStrength = this.uIndirectAngleStrength;
    const uIndirectAngleOffset = this.uIndirectAngleOffset;
    const uIndirectAnglePower = this.uIndirectAnglePower;
    const uIndirectColor = this.uIndirectColor;
    const uGroundY = this.uGroundY;

    const distAboveGround = positionWorld.y.sub(uGroundY);
    let indirectDistanceStrength = float(1.0)
      .sub(clamp(distAboveGround.div(uIndirectDistanceAmplitude), float(0.0), float(1.0)))
      .mul(uIndirectDistanceStrength);
    indirectDistanceStrength = pow(clamp(indirectDistanceStrength, float(0.0), float(1.0)), uIndirectDistancePower);

    const up = vec3(0.0, -1.0, 0.0);
    let indirectAngleStrength = dot(normalize(normalWorld), up).add(uIndirectAngleOffset);
    indirectAngleStrength = clamp(indirectAngleStrength.mul(uIndirectAngleStrength), float(0.0), float(1.0));
    indirectAngleStrength = pow(indirectAngleStrength, uIndirectAnglePower);

    const indirectStrength = indirectDistanceStrength.mul(indirectAngleStrength);
    diffuseColor.rgb.assign(mix(diffuseColor.rgb, uIndirectColor, indirectStrength));
  }
}
